import React from "react"
import PropTypes from "prop-types"
import { getPatientAge, getPatientName, getPatientMRN, getViewPatientData } from "../../../lib"
import "./PatientSidebar.less"
import Grid from "../../Fhir/Grid";

export default class PatientSidebar extends React.Component {
    static propTypes = {
        patient: PropTypes.object.isRequired,
        isOpen: PropTypes.bool.isRequired,
        onClose: PropTypes.func.isRequired,
        settings: PropTypes.object.isRequired
    };

    constructor(props) {
        super(props);
        this.state = {
            activeTab: "allergy", // Default active tab
            allergyData: [],
            diagnosisData: [],
            loading: false,
            error: null
        };
    }

    componentDidMount() {
        this.fetchTabData(this.state.activeTab);
    }

    componentDidUpdate(prevProps) {
        // Fetch data when sidebar opens and patient changes
        if (this.props.isOpen && !prevProps.isOpen && this.props.patient) {
            this.fetchTabData(this.state.activeTab);
        }

        if (this.props.patient !== prevProps.patient && this.props.isOpen) {
            this.fetchTabData(this.state.activeTab);
        }
    }

    fetchTabData = async (tabName) => {
        if (!this.props.patient) return;

        this.setState({ loading: true, error: null });

        try {
            let apiUrl;
            const { server } = this.props.settings;

            if (tabName === "allergy") {
                apiUrl = `${server.url}/allergy-list?id=${this.props.patient?.allergyTableKey}`;
            } else if (tabName === "diagnosis") {
                apiUrl = `${server.url}/diagnosis-list?id=${this.props.patient?.diagnosisTableKey}`;
            }

            const data = await getViewPatientData({
                url: apiUrl,
                server: this.props.settings.server
            });

            if (tabName === "allergy") {
                this.setState({ allergyData: data || [], loading: false });
            } else if (tabName === "diagnosis") {
                this.setState({ diagnosisData: data || [], loading: false });
            }
        } catch (error) {
            console.error(`Error fetching ${tabName} data:`, error);
            this.setState({
                error: error.message,
                loading: false,
                [`${tabName}Data`]: []
            });
        }
    }

    handleTabChange = (tabName) => {
        this.setState({ activeTab: tabName }, () => {
            this.fetchTabData(tabName);
        });
    }

    renderPatientDetails() {
        const { patient } = this.props;

        return (
            <div>
                <div className="patient-basic-info">
                    <h3 className="text-primary">
                        {patient.patientFirstName && patient.patientLastName ?
                            `${patient.patientFirstName} ${patient.patientLastName}` :
                            getPatientName(this.state.patient) || "Unknown Patient"
                        }
                    </h3>
                    <div className="patient-details-grid">
                        <div className="detail-item">
                            <span className="label">Age:</span>
                            <span className="value">{getPatientAge(patient.patientDOB || patient.birthDate) || "Unknown"}</span>
                        </div>
                        <div className="detail-item">
                            <span className="label">DOB:</span>
                            <span className="value">{patient.patientDOB || patient.birthDate || "Unknown"}</span>
                        </div>
                        <div className="detail-item">
                            <span className="label">Gender:</span>
                            <span className="value">{patient.patientGender || patient.gender || "Unknown"}</span>
                        </div>
                        <div className="detail-item">
                            <span className="label">MRN:</span>
                            <span className="value">{getPatientMRN(patient) || patient.patientMRN || "Unknown"}</span>
                        </div>
                        <div className="detail-item">
                            <span className="label">ID:</span>
                            <span className="value">{patient.messageID || patient.id || "Unknown"}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderTabContent() {
        const { activeTab, loading, error, allergyData, diagnosisData } = this.state;

        if (loading) {
            return (
                <div className="tab-loading">
                    <i className="fa fa-spinner fa-spin" /> Loading...
                </div>
            );
        }

        if (error) {
            return (
                <div className="tab-error">
                    <p className="text-danger">Error: {error}</p>
                </div>
            );
        }

        switch (activeTab) {
            case "allergy":
                return this.renderAllergyContent(allergyData);
            case "diagnosis":
                return this.renderDiagnosisContent(diagnosisData);
            default:
                return null;
        }
    }

    renderAllergyContent(data) {
        if (!data || data.length === 0) {
            return <p className="text-muted">No allergy data available.</p>;
        }

        return (
            <div className="tab-content-wrapper">
                <Grid
                    rows={(data || [])}
                    title="Allergy"
                    cols={[
                        {
                            label: "Code",
                            path: "allergyCode"
                        },
                        {
                            label: "Description",
                            path: "allergyDescription"
                        },
                        {
                            label: "CodeSystem",
                            path: "allergyCodeSystem"
                        }
                    ]}
                />
            </div>
        );
    }

    renderDiagnosisContent(data) {
        if (!data || data.length === 0) {
            return <p className="text-muted">No diagnosis data available.</p>;
        }

        return (
            <div className="tab-content-wrapper">
                <Grid
                    rows={data || []}
                    title="Diagnosis"
                    cols={[
                        {
                            label: "Code",
                            path: "diagnosisCode"
                        },
                        {
                            label: "Description",
                            path: "diagnosisDescription"
                        },
                        {
                            label: "CodeSystem",
                            path: "diagnosisCodeSystem"
                        }
                    ]}
                />
            </div>
        );
    }

    render() {
        const { isOpen, onClose } = this.props;
        const { activeTab } = this.state;

        console.log("PatientSidebar render - isOpen:", isOpen);
        console.log("PatientSidebar render - patient:", this.props.patient);

        if (!isOpen) {
            console.log("PatientSidebar not rendering because isOpen is false");
            return null;
        }

        if (!isOpen) return null;

        return (
            <div className="patient-sidebar-overlay" onClick={onClose}>
                <div className="patient-sidebar" onClick={e => e.stopPropagation()}>
                    {/* Header */}
                    <div className="sidebar-header">
                        <h4>Patient Details</h4>
                        <button className="close-btn" onClick={onClose}>
                            <i className="fa fa-times" />
                        </button>
                    </div>

                    {/* Patient Details */}
                    <div className="sidebar-patient-details">
                        {this.renderPatientDetails()}
                    </div>

                    {/* Tabs Navigation */}
                    <div className="sidebar-tabs">
                        <div className="tabs-header">
                            <button
                                className={`tab-btn ${activeTab === "allergy" ? "active" : ""}`}
                                onClick={() => this.handleTabChange("allergy")}
                            >
                                Allergy
                            </button>
                            <button
                                className={`tab-btn ${activeTab === "diagnosis" ? "active" : ""}`}
                                onClick={() => this.handleTabChange("diagnosis")}
                            >
                                Diagnosis
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="tabs-content">
                            {this.renderTabContent()}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}